import { tenantApi } from '@/modules/tenant/services/tenantApi';
import type { Boat, BoatFormData } from '../types/boat.types';

export interface BrandSuggestion {
  outcome: string;
  suggestion: string | null;
  score: number | null;
}

export const boatApi = {
  // Get all boats with filters (paginated)
  getBoats: (params: {
    page?: number;
    per_page?: number;
    search?: string;
    condition?: string;
    // Phase 50: was availability_status. Backend keeps both filters
    // for one release cycle per Plan 50-02 §13.
    website_status?: string | string[];
    status_id?: string | string[];
    manufacturer?: string | string[];
    min_price?: number;
    max_price?: number;
    sort_by?: string;
    sort_order?: string;
    location_id?: string | string[];
  }) => tenantApi.getPaginated<Boat>('/boats', { params }),

  // Get single boat
  getBoat: (id: number | string) =>
    tenantApi.getOne<Boat>(`/boats/${id}`),

  // Create boat
  createBoat: (boatData: BoatFormData) =>
    tenantApi.postOne<Boat>('/boats', boatData),

  // Update boat
  updateBoat: (id: number | string, boatData: Partial<BoatFormData>) =>
    tenantApi.putOne<Boat>(`/boats/${id}`, boatData),

  /**
   * #1177 — a changes-only update built by utils/boat-changes (it always carries
   * `version`). Returns the saved boat.
   */
  updateBoatChanges: (id: string, payload: Record<string, unknown>) =>
    tenantApi.putOne<Boat>(`/boats/${id}`, payload),

  // Delete boat
  deleteBoat: (id: number | string) =>
    tenantApi.deleteOne(`/boats/${id}`),

  // SP2-C — engine/trailer link management (attach/swap return the refreshed boat)
  attachAsset: (boatId: string, assetId: string) =>
    tenantApi.postOne<Boat>(`/boats/${boatId}/assets`, { asset_id: assetId }),
  detachAsset: (boatId: string, assetId: string) =>
    tenantApi.deleteOne(`/boats/${boatId}/assets/${assetId}`),
  swapAsset: (boatId: string, assetId: string, replacementAssetId: string) =>
    tenantApi.putOne<Boat>(`/boats/${boatId}/assets/${assetId}/swap`, {
      replacement_asset_id: replacementAssetId,
    }),

  // Get field options for autocomplete
  getFieldOptions: async (field: string, search?: string, manufacturer?: string): Promise<string[]> => {
    const params = new URLSearchParams({ field });
    if (search) params.append('search', search);
    if (manufacturer) params.append('manufacturer', manufacturer);
    return tenantApi.getList<string>(`/field-options?${params}`);
  },

  // Resolve a typed make/model/engine-mfr against the canonical catalog for the
  // wizard "did you mean?" chip. Returns the mid-band Suggest near-match (or null).
  resolveBrand: (
    field: 'manufacturer' | 'model' | 'engine_manufacturer',
    value: string,
    manufacturer?: string,
  ): Promise<BrandSuggestion> => {
    const params = new URLSearchParams({ field, value });
    if (manufacturer) params.append('manufacturer', manufacturer);
    return tenantApi.getOne<BrandSuggestion>(`/brands/resolve?${params}`);
  },

  // Search boats (paginated)
  searchBoats: (query: string, perPage = 15) =>
    tenantApi.getPaginated<Boat>('/boats/search', {
      params: { query, per_page: perPage },
    }),

  // Mark boat as sold — all sale details are optional; staff can flag
  // a boat sold and fill in price/customer/date later.
  // Phase 50: hide_from_website (default true server-side) flips
  // website_status='disabled' when truthy.
  markAsSold: (id: number | string, saleData: {
    sold_price?: number;
    sold_date?: string;
    sold_to_customer_id?: string;
    hide_from_website?: boolean;
  }) => tenantApi.postOne<Boat>(`/boats/${id}/mark-sold`, saleData),

  // #607 — publish to website. Dedicated endpoint, NOT a partial updateBoat():
  // a partial PUT runs the server's buildItemData(), which defaults `featured`
  // and `is_on_sale` to false and silently clears both on a live listing.
  publishToWebsite: (id: number | string) =>
    tenantApi.postOne<Boat>(`/boats/${id}/publish`, {}),
};
